import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Grid, Paper, Typography, Button, TextField, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Alert, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import { useApi } from '../contexts/ApiContext';

// Platform B Bank Reconciliation — Switch CASH deposits vs settled cash (2pm–2pm Central window).
// API: GET /api/v1/pai/settings?view=bankReconciliation, PUT /api/v1/pai/settings { action: 'bankDeposit.*' }.
// Spec: docs/superpowers/specs/2026-09-12-platform-b-bank-reconciliation-design.md

const money = (n) => (n === null || n === undefined) ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const int = (n) => (n === null || n === undefined) ? '—' : Number(n).toLocaleString();
const pct = (n) => (n === null || n === undefined) ? '—' : `${Number(n).toFixed(2)}%`;
const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };
const csvCell = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };

const STATUS_STYLE = {
  MATCHED:  { color: 'success', bg: '#e8f5e9', label: 'Matched' },
  ACCEPTED: { color: 'success', bg: '#e8f5e9', label: 'Accepted' },
  SHORT:    { color: 'error',   bg: '#ffebee', label: 'Short' },
  OVER:     { color: 'warning', bg: '#fff8e1', label: 'Over' },
  PENDING:  { color: 'default', bg: '#f5f5f5', label: 'Pending' },
};

function SummaryCard({ label, value, color, bg }) {
  return (
    <Grid item xs={6} sm={4} md={2.4}>
      <Paper elevation={1} sx={{ p: 2, textAlign: 'center', backgroundColor: bg || '#f5f5f5' }}>
        <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
        <Typography variant="h6" color={color || 'text.primary'}>{value}</Typography>
      </Paper>
    </Grid>
  );
}

export default function BankReconciliationTab() {
  const { apiCall } = useApi();
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(iso(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ businessDate: '', amount: '', postingDate: '', note: '' });
  const [saving, setSaving] = useState(false);
  const [acceptFor, setAcceptFor] = useState(null);   // row being accepted
  const [acceptNote, setAcceptNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await apiCall('/api/v1/pai/settings', 'GET', null, { view: 'bankReconciliation', startDate, endDate });
      setData(res);
      // Pre-fill the form with the earliest day that has settled cash but no deposit.
      const pending = [...(res.rows || [])].filter(r => r.status === 'PENDING').sort((a, b) => (a.businessDate < b.businessDate ? -1 : 1));
      setForm(f => f.businessDate ? f : { businessDate: pending[0]?.businessDate || endDate, amount: '', postingDate: '', note: '' });
    } catch (e) {
      setError(e.message || 'Failed to load bank reconciliation');
    } finally { setLoading(false); }
  }, [apiCall, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const act = async (body) => {
    setSaving(true); setError('');
    try { await apiCall('/api/v1/pai/settings', 'PUT', body); await load(); return true; }
    catch (e) { setError(e.message || 'Request failed'); return false; }
    finally { setSaving(false); }
  };

  const handleSave = async () => {
    const ok = await act({ action: 'bankDeposit.upsert', businessDate: form.businessDate, amount: Number(form.amount),
      postingDate: form.postingDate || null, note: form.note || null });
    if (ok) setForm({ businessDate: '', amount: '', postingDate: '', note: '' });
  };
  const handleEdit = (r) => setForm({ businessDate: r.businessDate, amount: String(r.deposit ?? ''), postingDate: r.postingDate || '', note: r.note || '' });
  const handleDelete = async (r) => {
    if (!window.confirm(`Delete the deposit entered for ${r.businessDate} (${money(r.deposit)})? The day goes back to Pending.`)) return;
    await act({ action: 'bankDeposit.delete', businessDate: r.businessDate });
  };
  const handleAccept = async () => {
    const ok = await act({ action: 'bankDeposit.accept', businessDate: acceptFor.businessDate, acceptedNote: acceptNote });
    if (ok) { setAcceptFor(null); setAcceptNote(''); }
  };
  const handleUnaccept = (r) => act({ action: 'bankDeposit.unaccept', businessDate: r.businessDate });

  const s = data?.summary || {};
  const rows = useMemo(() => data?.rows || [], [data]);
  const sinceBreak = useMemo(() => rows.filter(r => r.businessDate >= '2026-08-24').reduce((a, r) => a + (r.excess || 0), 0), [rows]);

  const exportCsv = () => {
    const header = ['Business Day', 'Cash Txns', 'Settled Cash', 'Deposit', 'Posting Date', 'Difference', 'Difference %', 'Expected Fee', 'Excess', 'Status', 'Note', 'Accepted By', 'Accepted Note', 'Source'];
    const lines = [header.join(',')].concat(rows.map(r => [r.businessDate, r.cashTxns, r.settledCash.toFixed(2), r.deposit === null ? '' : r.deposit.toFixed(2), r.postingDate || '',
      r.difference === null ? '' : r.difference.toFixed(2), r.differencePct === null ? '' : r.differencePct.toFixed(2), r.expectedFee.toFixed(2), r.excess.toFixed(2), r.status,
      r.note || '', r.acceptedBy || '', r.acceptedNote || '', r.source || ''].map(csvCell).join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `bank_reconciliation_platformB_${startDate}_to_${endDate}.csv`; a.click(); URL.revokeObjectURL(a.href);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        Settled cash is what Switch settled in its 2pm-to-2pm Central business day (from Switch's own transaction file). Switch's normal fee is
        {` ${data?.expectedFeeRate ?? 1.3}%`}; a day short by more than that stays red until you Accept it with a note.
      </Alert>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <SummaryCard label="Days open (Short)" value={int(s.open)} color={s.open ? 'error' : 'text.primary'} bg={s.open ? '#ffebee' : undefined} />
        <SummaryCard label="Unexplained excess" value={money(s.unexplainedExcess)} color="error" bg="#ffebee" />
        <SummaryCard label="Excess since 8/24 (all days)" value={money(sinceBreak)} />
        <SummaryCard label="Settled cash" value={money(s.settledCash)} />
        <SummaryCard label="Deposits" value={money(s.deposits)} />
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Enter a Switch deposit</Typography>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={6} md={2.5}><TextField label="Switch business day" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.businessDate} onChange={e => setForm({ ...form, businessDate: e.target.value })} /></Grid>
          <Grid item xs={12} sm={6} md={2.5}><TextField label="Deposit amount" type="number" size="small" fullWidth inputProps={{ step: '0.01', min: '0' }} value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} /></Grid>
          <Grid item xs={12} sm={6} md={2.5}><TextField label="Bank posting date" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.postingDate} onChange={e => setForm({ ...form, postingDate: e.target.value })} /></Grid>
          <Grid item xs={12} sm={6} md={3}><TextField label="Note" size="small" fullWidth value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} /></Grid>
          <Grid item xs={12} md={1.5}><Button variant="contained" fullWidth disabled={saving || !form.businessDate || form.amount === ''} onClick={handleSave}>Save</Button></Grid>
        </Grid>
      </Paper>

      <Box display="flex" gap={1} alignItems="center" sx={{ mb: 2 }}>
        <TextField label="From" type="date" size="small" InputLabelProps={{ shrink: true }} value={startDate} onChange={e => setStartDate(e.target.value)} />
        <TextField label="To" type="date" size="small" InputLabelProps={{ shrink: true }} value={endDate} onChange={e => setEndDate(e.target.value)} />
        <Button variant="outlined" startIcon={<GetAppIcon />} onClick={exportCsv} disabled={rows.length === 0}>Export CSV</Button>
        {loading && <CircularProgress size={20} />}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Business day</TableCell>
            <TableCell align="right">Cash txns</TableCell>
            <TableCell align="right">Settled cash</TableCell>
            <TableCell align="right">Deposit</TableCell>
            <TableCell>Posted</TableCell>
            <TableCell align="right">Difference</TableCell>
            <TableCell align="right">% of settled</TableCell>
            <TableCell align="right">Expected fee</TableCell>
            <TableCell align="right">Excess</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Note</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(r => {
            const st = STATUS_STYLE[r.status] || STATUS_STYLE.PENDING;
            return (
              <TableRow key={r.businessDate} hover sx={{ backgroundColor: st.bg }}>
                <TableCell>{r.businessDate}</TableCell>
                <TableCell align="right">{int(r.cashTxns)}</TableCell>
                <TableCell align="right">{money(r.settledCash)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{money(r.deposit)}</TableCell>
                <TableCell>{r.postingDate || '—'}</TableCell>
                <TableCell align="right">{money(r.difference)}</TableCell>
                <TableCell align="right">{pct(r.differencePct)}</TableCell>
                <TableCell align="right">{money(r.expectedFee)}</TableCell>
                <TableCell align="right" sx={{ color: r.excess > 0 ? 'error.main' : undefined }}>{money(r.excess)}</TableCell>
                <TableCell>
                  <Tooltip title={r.status === 'ACCEPTED' ? `${r.acceptedBy}: ${r.acceptedNote}` : ''}>
                    <Chip size="small" color={st.color} variant={r.status === 'PENDING' ? 'outlined' : 'filled'} label={st.label} />
                  </Tooltip>
                  {r.source === 'statement-backfill' && <Chip size="small" variant="outlined" label="statement" sx={{ ml: 0.5 }} />}
                </TableCell>
                <TableCell sx={{ maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.note || ''}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {r.deposit !== null && <IconButton size="small" onClick={() => handleEdit(r)} aria-label="edit"><EditIcon fontSize="small" /></IconButton>}
                  {r.deposit !== null && <IconButton size="small" onClick={() => handleDelete(r)} aria-label="delete"><DeleteIcon fontSize="small" /></IconButton>}
                  {r.status === 'SHORT' && <IconButton size="small" color="success" onClick={() => { setAcceptFor(r); setAcceptNote(''); }} aria-label="accept"><CheckCircleIcon fontSize="small" /></IconButton>}
                  {r.status === 'ACCEPTED' && <IconButton size="small" onClick={() => handleUnaccept(r)} aria-label="unaccept"><UndoIcon fontSize="small" /></IconButton>}
                </TableCell>
              </TableRow>
            );
          })}
          {rows.length === 0 && !loading && (
            <TableRow><TableCell colSpan={12} align="center" sx={{ color: 'text.secondary' }}>No settled cash or deposits in this range.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={!!acceptFor} onClose={() => setAcceptFor(null)} fullWidth maxWidth="sm">
        <DialogTitle>Accept shortfall for {acceptFor?.businessDate}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Deposit {money(acceptFor?.deposit)} against settled {money(acceptFor?.settledCash)}: short {money(acceptFor?.difference)},
            of which {money(acceptFor?.excess)} is beyond the expected fee. A note is required.
          </Typography>
          <TextField autoFocus fullWidth multiline minRows={2} label="Why this is accepted" value={acceptNote} onChange={e => setAcceptNote(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcceptFor(null)}>Cancel</Button>
          <Button variant="contained" disabled={saving || !acceptNote.trim()} onClick={handleAccept}>Accept</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
